import React, { useState } from 'react';
import { Button, InputNumber } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { chartFormGlyph } from '../reports/chartFormGlyphs';
import { chartFormWord } from '../../utils/chartForms';
import { LIMITS } from '../../utils/analytics';
import BuilderStep from './BuilderStep';
import { ORIENTATION_WORDS, SORT_WORDS, STACK_WORDS } from './builderModel';
import { Field, SegmentedField } from './controls';
import FieldMessage, { messageProps } from './FieldMessage';

const wordsAsOptions = (words) => Object.keys(words).map((value) => ({ value, label: words[value] }));

/** A switch that may be unavailable for this question: off and disabled, with the reason in place. */
function OptionToggle({ id, label, description, rule, checked, onChange, messages }) {
	return (
		<>
			<ToggleRow
				id={id}
				label={label}
				description={rule && rule.offered === false ? rule.reason : description}
				checked={rule && rule.offered === false ? false : checked}
				disabled={Boolean(rule && rule.offered === false)}
				onChange={onChange}
			/>
			<FieldMessage messages={messages} id={`${id}-messages`} />
		</>
	);
}

/**
 * Steps 7 and 8, Show as and its options: the forms this question may be
 * drawn as (the capability table's formsForShape over the visible measures),
 * the recommended one first and marked — it is picked for you until you pick
 * one — and the chosen form's own options (§7): Lower is better, a sparkline,
 * a progress meter's goal, a bar's direction, stacking and order, a line's
 * running total and markers, and the comparison with the previous period.
 * An option this question cannot take says why where it stands. The forms it
 * cannot be drawn as are one disclosure away, each with its reason.
 */
export default function ShowStep({ draft, view, reasons, onShow, onTime, messagesFor, step }) {
	const [whyOpen, setWhyOpen] = useState(false);
	const rules = view.formOptions;
	const show = draft.show;
	const typeMessages = messagesFor('show.type');
	const goalMessages = messagesFor('show.goal');
	const hasOptions = Object.keys(rules).length > 0;
	return (
		<BuilderStep step='show' number={7} title='Show as' {...step}>
			<div role='radiogroup' aria-label='Show as' className='chart-builder__forms' aria-describedby={typeMessages.length ? 'chart-builder-form-messages' : undefined}>
				{view.forms.map((form) => {
					const Glyph = chartFormGlyph(form);
					const selected = show.type === form;
					return (
						<button
							key={form}
							type='button'
							role='radio'
							aria-checked={selected ? 'true' : 'false'}
							className={`chart-builder__form${selected ? ' chart-builder__form--selected' : ''}`}
							onClick={() => onShow({ type: form, chosen: true })}
							data-form={form}
						>
							<Glyph aria-hidden='true' className='chart-builder__form-glyph' />
							<span className='chart-builder__form-word'>{chartFormWord(form)}</span>
							{form === view.recommended ? <span className='chart-builder__form-recommended'>Recommended</span> : null}
						</button>
					);
				})}
			</div>
			<FieldMessage messages={typeMessages} id='chart-builder-form-messages' />
			{reasons.length ? (
				<div className='chart-builder__why'>
					<Button
						type='link'
						size='small'
						className='chart-builder__why-toggle'
						aria-expanded={whyOpen ? 'true' : 'false'}
						aria-controls='chart-builder-why-forms'
						onClick={() => setWhyOpen((open) => !open)}
					>
						{whyOpen ? 'Hide the other forms' : 'Why not other forms?'}
					</Button>
					{whyOpen ? (
						<ul className='chart-builder__why-list' id='chart-builder-why-forms'>
							{reasons.map((r) => (
								<li key={r.form}>
									<span className='chart-builder__why-form'>{r.word}</span>
									{` — ${r.reason}`}
								</li>
							))}
						</ul>
					) : null}
				</div>
			) : null}

			{hasOptions ? <div className='micro-label chart-builder__options-kicker'>8 · Options</div> : null}
			{rules.goal ? (
				<Field id='chart-builder-goal' label='Goal' messages={goalMessages} hint={view.goal.kind === 'share' ? 'A share, in percent.' : null}>
					<InputNumber
						id='chart-builder-goal'
						className='chart-builder__number chart-builder__goal'
						value={show.goal}
						max={LIMITS.goalAbs}
						min={-LIMITS.goalAbs}
						addonAfter={view.goal.suffix || undefined}
						onChange={(value) => onShow({ goal: typeof value === 'number' && Number.isFinite(value) ? value : null })}
						{...messageProps(goalMessages, 'chart-builder-goal-messages')}
					/>
				</Field>
			) : null}
			{rules.lowerIsBetter ? (
				<OptionToggle
					id='chart-builder-lower-is-better'
					label='Lower is better'
					description='A fall reads as good news: a wait time, a cost.'
					rule={rules.lowerIsBetter}
					checked={show.lowerIsBetter}
					onChange={(on) => onShow({ lowerIsBetter: on })}
					messages={messagesFor('show.lowerIsBetter')}
				/>
			) : null}
			{rules.sparkline ? (
				<OptionToggle
					id='chart-builder-sparkline'
					label='Sparkline'
					description="A small line of the measure across the chart's dates."
					rule={rules.sparkline}
					checked={show.sparkline}
					onChange={(on) => onShow({ sparkline: on })}
					messages={messagesFor('show.sparkline')}
				/>
			) : null}
			{rules.compare ? (
				<OptionToggle
					id='chart-builder-compare'
					label='Compare with the previous period'
					description='The same length of time just before the range, beside it.'
					rule={rules.compare}
					checked={draft.time.compare}
					onChange={(on) => onTime({ compare: on })}
					messages={messagesFor('time.compare')}
				/>
			) : null}
			{rules.orientation ? (
				<SegmentedField
					id='chart-builder-orientation'
					label='Bars'
					value={show.orientation}
					options={wordsAsOptions(ORIENTATION_WORDS)}
					onChange={(orientation) => onShow({ orientation })}
					messages={messagesFor('show.orientation')}
					hint={show.orientation === 'auto' ? 'Across past six values or long names, upright otherwise.' : null}
				/>
			) : null}
			{rules.stack ? (
				<SegmentedField
					id='chart-builder-stack'
					label='Parts'
					value={rules.stack.offered ? show.stack : 'none'}
					options={wordsAsOptions(STACK_WORDS)}
					onChange={(stack) => onShow({ stack })}
					disabled={!rules.stack.offered}
					messages={messagesFor('show.stack')}
					hint={rules.stack.offered ? null : rules.stack.reason}
				/>
			) : null}
			{rules.sort ? (
				<SegmentedField
					id='chart-builder-sort'
					label='Order'
					value={show.sort}
					options={wordsAsOptions(SORT_WORDS)}
					onChange={(sort) => onShow({ sort })}
					messages={messagesFor('show.sort')}
				/>
			) : null}
			{rules.cumulative ? (
				<OptionToggle
					id='chart-builder-cumulative'
					label='Running total'
					description='Each point adds everything before it.'
					rule={rules.cumulative}
					checked={show.cumulative}
					onChange={(on) => onShow({ cumulative: on })}
					messages={messagesFor('show.cumulative')}
				/>
			) : null}
			{rules.markers ? (
				<OptionToggle
					id='chart-builder-markers'
					label='Mark each point'
					description='A dot on every value of the line.'
					rule={rules.markers}
					checked={show.markers}
					onChange={(on) => onShow({ markers: on })}
					messages={messagesFor('show.markers')}
				/>
			) : null}
		</BuilderStep>
	);
}
